<template>
  <div
    class="form-input__wrapper"
    :class="{
      'has-error': hasError,
      'is-disabled': isDisabled,
      'has-tip': hasTipSlot,
      'relative-errors': relativeErrors,
      'custom-height': height
    }"
  >
    <div v-if="label || hasTipSlot || hasFixedTipSlot" class="form-input__top">
      <label v-if="label" :for="id" class="form-input__label">
        {{ label }}
        <span v-if="mark" class="form-input__mark">
          {{ mark }}
        </span>
      </label>

      <span v-if="hasTipSlot" class="form-input__tip">
        <slot name="tip" />
      </span>

      <span v-if="hasFixedTipSlot" class="form-input__fixed-tip">
        <slot name="fixedTip" />
      </span>
    </div>

    <slot
        :attrs="attrs"
        :listeners="listeners"
    >
      <input
          ref="input"
          v-bind="attrs"
          v-on="listeners"
      >
    </slot>

    <div v-if="logo" class="form-input__logo">
      <card-type-icon :type="logo" />
    </div>
    <div v-if="showCounter" class="form-input__counter">
      {{ valueLengthCounter }}
    </div>

    <div class="form-input__comment">
      <slot v-if="hasCommentSlot" name="comment" />
    </div>

    <span v-if="hasError" class="form-input__error" v-html="error" />
  </div>
</template>

<script>
import CardTypeIcon from "../CardTypeIcon.vue";

export default {
  name: 'FormInput',
  components: { CardTypeIcon },

  props: {
    id: { type: String, required: false, default: '' },
    name: { type: String, required: false, default: '' },
    placeholder: { type: String, required: false, default: '' },
    type: { type: String, required: false, default: 'text' },
    inputmode: { type: String, required: false, default: 'text' },
    label: { type: String, required: false, default: '' },
    mark: { type: String, required: false, default: '' },
    hasError: { type: Boolean, required: false, default: false },
    error: { type: String, required: false, default: '' },
    value: { type: [String, Number], required: false, default: '' },
    modelValue: { type: [String, Number], required: false, default: undefined },
    isRegular: { type: Boolean, required: false, default: false },
    isDisabled: { type: Boolean, required: false, default: false },
    displayUppercase: { type: Boolean, required: false, default: false },
    minLength: { type: String, required: false },
    maxLength: { type: String, required: false },
    showCounter: { type: Boolean, required: false, default: false },
    min: { type: String, required: false },
    max: { type: String, required: false },
    step: { type: String, required: false },
    required: { type: Boolean, required: false },
    autocomplete: { type: Boolean, required: false, default: true },
    logo: { type: String, required: false, default: '' },
    dataTestid: { type: String, required: false, default: null },
    focus: { type: Boolean, required: false, default: false },
    relativeErrors: { type: Boolean, required: false, default: false },
    height: { type: String, required: false, default: false },
  },

  emits: ['focus', 'input', 'update:modelValue'],

  data() {
    return {
      customHeight: `${this.height}px`,
    }
  },

  computed: {
    currentValue() {
      return this.modelValue === undefined ? this.value : this.modelValue;
    },

    valueLengthCounter() {
      if (!this.showCounter || !this.maxLength || this.type !== 'text') {
        return 0;
      }

      if (!this.currentValue) {
        return this.maxLength;
      }

      return Number.parseInt(this.maxLength) - this.currentValue.toString().length;
    },

    hasTipSlot() {
      return !!this.$slots.tip;
    },

    hasFixedTipSlot() {
      return !!this.$slots.fixedTip;
    },

    hasCommentSlot() {
      return !!this.$slots.comment;
    },

    isAutocomplete() {
      if (!this.autocomplete) {
        return 'off';
      }

      return null;
    },
    attrs() {
      return {
        id: this.id,
        value: this.currentValue,
        name: this.name,
        type: this.type,
        inputmode: this.inputmode,
        placeholder: this.placeholder,
        disabled:this.isDisabled,
        class:{'form-input__input': true, 'is-regular': this.isRegular, 'is-uppercase': this.displayUppercase },
        minlength: this.minLength,
        maxlength: this.maxLength,
        min: this.min,
        max: this.max,
        step: this.step,
        required: this.required,
        autocomplete: this.isAutocomplete,
        'data-testid': this.dataTestid,
      };
    },
    listeners() {
      return {
        focus: () => this.$emit('focus'),
        blur: () => this.$emit('blur'),
        input: ($event) => {
            this.$emit('input', $event.target.value);
            this.$emit('update:modelValue', $event.target.value);
          },
      }
    }
  },

  methods: {
    disableAutoComplete() {
      const element = this.$refs.input;
      if (element.getAttribute('autocomplete')) {
        // HACK: disable autocomplete
        element.setAttribute('readonly', 'readonly');
        element.style.backgroundColor = 'inherit';

        setTimeout(() => {
          element.removeAttribute('readonly');
        }, 500);
      }
    },
  },

  mounted() {
    if (this.$refs.input) {
      this.disableAutoComplete();
    }

    if (this.$refs.input && this.focus) {
      this.$refs.input.focus();
    }
  },

  watch: {
    focus(newFocus) {
      if (!this.$refs.input || !newFocus) {
        return;
      }

      const inputRef = this.$refs.input;

      inputRef.focus();
      inputRef.select();
    },
  },
};
</script>

<style lang="scss">
@use 'scss-vars';

.form-input {
  &__wrapper {
    width: 100%;
    position: relative;
  }

  &__wrapper.custom-height .form-input__input {
    height: v-bind(customHeight);
  }

  &__logo {
    display: block;
    position: relative;

    img {
      position: absolute;
      right: 16px;
      bottom: 12px;
    }
  }

  &__counter {
    position: absolute;
    right: 16px;
    top: 13px;
    font-family: scss-vars.$font-regular;
    color: scss-vars.$grey-30;
  }

  &__wrapper.is-small {
    display: flex;
    align-items: center;
  }

  &__top {
    display: flex;
    justify-content: space-between;
  }

  &__label {
    font-size: 16px;
    line-height: 1.25;
  }

  &__mark {
    margin-left: 2px;
    color: scss-vars.$grey-30;
  }

  &__tip {
    color: scss-vars.$grey-30;
    font-size: 14px;
    line-height: 1.14;
  }

  .is-disabled &__label {
    color: scss-vars.$grey-30;
  }

  &__top + &__input {
    margin-top: 4px;
  }

  &__input {
    display: block;
    width: 100%;
    background-color: scss-vars.$white;
    border: none;
    border-radius: 6px;
    font-family: scss-vars.$font-bold;
    font-size: 16px;
    line-height: 1.25;
    padding: 14px;
    box-shadow: 0 0 0 2px scss-vars.$black-10;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    transition: box-shadow 0.2s linear;
  }

  &__input:hover {
    box-shadow: 0 0 0 2px scss-vars.$black-20;
  }

  &__input:focus {
    outline: none;
    box-shadow: 0 0 0 1px scss-vars.$violet-60;
  }

  &__input:disabled {
    background-color: scss-vars.$grey-04;
    color: scss-vars.$grey;
    box-shadow: 0 0 0 2px scss-vars.$grey-04;
  }

  &__input.is-regular {
    font-family: scss-vars.$font-regular;
  }

  .is-small &__input {
    padding: 4px 6px;
    max-width: 52px;
  }

  .has-error &__input {
    box-shadow: 0 0 0 2px scss-vars.$red;
  }

  .has-error &__input:hover {
    box-shadow: 0 0 0 2px rgba(231, 60, 64, 0.25);
  }

  .relative-errors &__error {
   float: left;
  }

  &__input::-webkit-inner-spin-button,
  &__input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &__input::-ms-clear {
    display: none;
  }

  &__input::-webkit-input-placeholder {
    font-family: scss-vars.$font-regular;
    color: scss-vars.$grey-30;
  }

  &__input:-ms-input-placeholder {
    font-family: scss-vars.$font-regular;
    color: scss-vars.$grey-30;
  }

  &__input::placeholder {
    font-family: scss-vars.$font-regular;
    color: scss-vars.$grey-30;
  }

  &__error {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.33;
    color: scss-vars.$red;
  }

  @media screen and (max-width: scss-vars.$screen-md-max) {
    &__wrapper.has-tip {
      padding-bottom: 28px;
    }

    .has-tip &__tip {
      position: absolute;
      bottom: 2px;
    }

    .is-small &__error {
      margin-left: 8px;
    }
  }

  @media screen and (min-width: scss-vars.$screen-lg-min) {
    &__wrapper.is-small {
      flex-direction: column;
      align-items: flex-end;
    }
  }

  [dir='rtl'] & {
    &__logo {
      img {
        right: auto;
        left: 16px;
      }
    }
  }
}
</style>
